/**
 * useSfx — React glue around the SfxManager singleton
 *
 * Two main use cases:
 *  1. In a click handler: get `play` and `unlock` from the hook, then
 *     call `unlock()` and `play(id)` inside the handler. The unlock is
 *     what satisfies the autoplay policy.
 *  2. In a side-effect (e.g. on episode end): call `play(id)` directly
 *     — it will no-op if not yet unlocked, which is fine for non-gesture
 *     events.
 *
 * The hook is intentionally minimal. The manager itself is a plain
 * singleton; this hook just re-renders subscribers if the mute state
 * changes (so a mute toggle in the UI updates the indicator immediately).
 */

import { useCallback, useState } from 'react';
import { getSfxManager } from './SfxManager';
import type { SfxId } from './sfxCatalog';

export interface UseSfxReturn {
  /** Play an SFX. No-op if muted or not yet unlocked. */
  play: (id: SfxId, opts?: { volume?: number }) => boolean;
  /**
   * Unlock the AudioContext. MUST be called from a user gesture (e.g.
   * a click handler) before the first non-trivial `play()`.
   */
  unlock: () => Promise<void>;
  /** Current mute state. */
  muted: boolean;
  /** Toggle / set mute. */
  setMuted: (muted: boolean) => void;
}

export function useSfx(): UseSfxReturn {
  // Local mirror of mute so the consumer re-renders when it changes.
  // The manager is the source of truth — this is just a subscription.
  const manager = getSfxManager();
  const [muted, setMutedState] = useState<boolean>(manager.isMuted());

  const play = useCallback(
    (id: SfxId, opts?: { volume?: number }) => manager.play(id, opts),
    [manager],
  );

  const unlock = useCallback(async () => {
    await manager.unlock();
  }, [manager]);

  const setMuted = useCallback(
    (next: boolean) => {
      manager.setMuted(next);
      setMutedState(next);
    },
    [manager],
  );

  return { play, unlock, muted, setMuted };
}
